import React from 'react'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'success'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  children: React.ReactNode
}

export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-surface-50 transition-colors duration-200'
  
  const variantClasses = {
    primary:
      'bg-brand-primary-500 text-white hover:bg-brand-primary-600 focus:ring-brand-primary-500 disabled:bg-brand-primary-200 disabled:text-brand-primary-700',
    secondary:
      'bg-brand-accent-500 text-white hover:bg-brand-accent-600 focus:ring-brand-accent-500 disabled:bg-brand-accent-200',
    neutral:
      'bg-white text-brand-text-primary border border-brand-neutral-300 hover:bg-brand-surface-100 hover:border-brand-neutral-400 focus:ring-brand-neutral-500 disabled:bg-brand-surface-100 disabled:text-brand-text-muted',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-300',
    success:
      'bg-brand-primary-100 text-brand-primary-700 hover:bg-brand-primary-200 focus:ring-brand-primary-500 disabled:bg-brand-primary-50 disabled:text-brand-primary-300',
  }

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  }

  const classes = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    (disabled || loading) ? 'opacity-50 cursor-not-allowed' : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <button
      className={classes}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  )
}